<template>
    <l-map
        v-if="center.lat != null"
        ref="map"
        :zoom="getZoomLevel"
        :max-zoom="20"
        :min-zoom="8"
        :center="center"
        style="height: 100%; width: 100%"
        :options="options"
        @update:zoom="update_map({ event: $event, from: 'zoom' })"
        @update:center="update_map({ event: $event, from: 'center' })"
        @update:bounds="update_map({ event: $event, from: 'bounds' })"
    >
        <l-marker v-if="marker" :lat-lng="[center.lat, center.lng]" />
        <l-control-layers
            v-if="layersControlVisible"
            :collapsed="false"
            :sort-layers="true"
        />
        <l-tile-layer
            v-if="showAerialTiles"
            :url="aerialTileUrl"
            :options="tile_options"
            :name="$t('global.map.aerial_view')"
            layer-type="base"
        ></l-tile-layer>
        <l-tile-layer
            v-if="showCadastralTiles"
            :url="cadastralTileUrl"
            :options="{ maxZoom: 21, maxNativeZoom: 19 }"
            :name="$t('global.map.cadastral_view')"
            layer-type="base"
        ></l-tile-layer>
        <l-geo-json
            v-if="
                validGeoJsons.flood_regular_low_geojson &&
                    !$config.HIDDEN_FLOOD_TYPES.includes('regular')
            "
            :visible="floodVisible"
            :geojson="flood_regular_low_geojson"
            :options-style="style_flood"
            :name="$t('global.map.flood_zone_low')"
            layer-type="overlay"
        ></l-geo-json>
        <l-geo-json
            v-if="
                validGeoJsons.flood_regular_medium_geojson &&
                    !$config.HIDDEN_FLOOD_TYPES.includes('regular')
            "
            :visible="floodVisible"
            :geojson="flood_regular_medium_geojson"
            :options-style="style_flood"
            :name="$t('global.map.flood_zone_medium')"
            layer-type="overlay"
        ></l-geo-json>
        <l-geo-json
            v-if="
                validGeoJsons.flood_regular_high_geojson &&
                    !$config.HIDDEN_FLOOD_TYPES.includes('regular')
            "
            :visible="floodVisible"
            :geojson="flood_regular_high_geojson"
            :options-style="style_flood"
            :name="$t('global.map.flood_zone_high')"
            layer-type="overlay"
        ></l-geo-json>
        <l-geo-json
            v-if="
                validGeoJsons.flood_insurance_exclusion_geojson &&
                    !$config.HIDDEN_FLOOD_TYPES.includes('insurance_exclusion')
            "
            :visible="floodVisible"
            :geojson="flood_insurance_exclusion_geojson"
            :options-style="style_flood"
            :name="$t('global.map.exclusion_flood_zone')"
            layer-type="overlay"
        ></l-geo-json>
        <l-geo-json
            v-if="map_data_ready && validGeoJsons.parcel_geojson"
            ref="parcel_features"
            :geojson="parcel_geojson"
            :options="parcel_options"
        ></l-geo-json>
        <l-geo-json
            v-if="map_data_ready && validGeoJsons.building_geojson"
            ref="building_features"
            :geojson="building_geojson"
            :options="building_options"
        ></l-geo-json>
        <l-geo-json
            v-if="map_data_ready && size !== 'small' && validGeoJsons.address_geojson"
            :geojson="address_geojson"
            :options="address_options"
        ></l-geo-json>
        <l-geo-json
            v-if="validGeoJsons.swimming_pools_geojson"
            :geojson="swimming_pools_geojson"
            :options-style="style_swimming_pools"
            :name="$t('global.map.swimming_pools')"
            layer-type="overlay"
        ></l-geo-json>
        <l-tile-layer
            v-if="showStreetNames && showAerialTiles"
            :url="streetnameTileUrl"
            :options="{ maxZoom: 21, maxNativeZoom: 19 }"
            layer-type="overlay"
            :name="$t('global.map.street_name')"
        ></l-tile-layer>
        <slot name="custom-layer"></slot>
    </l-map>
</template>

<script>
import { circleMarker } from 'leaflet'
import { LControlLayers, LMap, LTileLayer, LGeoJson, LMarker } from 'vue2-leaflet'
import 'leaflet/dist/leaflet.css'
import axios from '@/shared/plugins/axios'
import debounce from 'lodash/debounce'

export default {
    components: {
        LControlLayers,
        LMap,
        LTileLayer,
        LGeoJson,
        LMarker,
    },
    props: {
        buildingId: {
            type: String,
            default: null,
        },
        lang: {
            type: String,
            default: 'fr',
            validator(value) {
                return ['en', 'fr', 'nl', 'en-BE', 'fr-BE', 'en-BE'].includes(value)
            },
        },
        level: {
            type: String,
            required: true,
            validator(value) {
                return ['0', '0.1', '0.2', '0.5', '0.7', '1', '2', '3'].includes(value)
            },
        },
        mapScene2d: {
            type: Object,
            default: function() {
                return {
                    building_geojson: null,
                    parcel_geojson: null,
                    address_geojson: null,
                    swimming_pool_geojson: null,
                    flood_geojson: null,
                }
            },
        },
        parcelIds: {
            type: Array,
            default: function() {
                return []
            },
        },
        autoSelectParcels: {
            type: Boolean,
            default: true,
        },
        center: {
            type: Object,
            default: function() {
                return {
                    lng: null,
                    lat: null,
                }
            },
        },
        readOnly: {
            type: Boolean,
            default: false,
        },
        marker: {
            type: Boolean,
            default: false,
        },
        forceUpdateParcels: {
            type: Boolean,
            default: false,
        },
        focusLayer: {
            type: String,
            default: 'building',
            validator(value) {
                return ['building', 'parcels', 'selected', 'flood'].includes(value)
            },
        },
        size: {
            type: String,
            default: 'full-page',
            validator(value) {
                return ['full-page', 'medium', 'fit', 'small'].includes(value)
            },
        },
        options: {
            type: Object,
            default: function() {
                return {
                    zoomControl: this.zoomVisible,
                }
            },
        },
        layersControlVisible: {
            type: Boolean,
            default: true,
        },
        backgroundTiles: {
            type: String,
            default: 'auto',
            validator(value) {
                return ['aerial', 'cadastral', 'auto', 'none'].includes(value)
            },
        },
        cadastralTileUrl: {
            type: String,
            default: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
        },
        floodVisible: {
            type: Boolean,
            default: true,
        },
        zoomVisible: {
            type: Boolean,
            default: true,
        },
        defaultZoomModifier: {
            type: Number,
            default: 0,
        },
    },
    data: function() {
        return {
            showStreetNames: true,
            internalZoom: null,
        }
    },
    computed: {
        aerialTileUrl() {
            if (this.region === 'Brussels' || this.region === 'Flanders') {
                return '/tiles/aerial_vlg/{z}/{x}/{y}.jpg'
            } else if (this.region === 'Wallonia') {
                return '/tiles/aerial_wal/{z}/{x}/{y}.jpg'
            } else {
                return `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v11/tiles/256/{z}/{x}/{y}?access_token=${this.$config.MAPBOX_TOKEN}`
            }
        },
        getZoomLevel() {
            let zoom = this.level < '0.5' ? 18 : 20
            if (this.level === '0') zoom = 8
            if (this.size == 'medium') zoom -= 1
            if (this.size == 'small') zoom -= 3
            if (this.focusLayer === 'flood') zoom -= 2
            if (this.defaultZoomModifier) zoom += this.defaultZoomModifier

            return zoom
        },
        zoom() {
            return this.internalZoom ?? this.getZoomLevel
        },
        showCadastralTiles() {
            return (
                this.backgroundTiles === 'cadastral' ||
                (this.backgroundTiles === 'auto' && this.zoom < 18)
            )
        },
        showAerialTiles() {
            return (
                this.backgroundTiles === 'aerial' ||
                (this.backgroundTiles === 'auto' && this.zoom >= 18)
            )
        },
        building_geojson() {
            let building_geojson = { ...this.mapScene2d.building_geojson }
            if (this.focusLayer == 'selected') {
                building_geojson.features = building_geojson.features.filter(
                    (feature) => feature.properties.building_id == this.buildingId
                )
            }
            return building_geojson
        },
        parcel_geojson() {
            let parcel_geojson = { ...this.mapScene2d.parcel_geojson }
            if (this.focusLayer == 'selected') {
                parcel_geojson.features = parcel_geojson.features.filter((feature) =>
                    this.parcelIds.includes(feature.properties.parcel_id)
                )
            }
            return parcel_geojson
        },
        address_geojson() {
            if (this.focusLayer == 'selected') {
                return {}
            }
            return this.mapScene2d.address_geojson
        },
        swimming_pools_geojson() {
            if (this.focusLayer == 'selected') {
                return {}
            }
            return this.mapScene2d.swimming_pools_geojson
        },
        flood_regular_low_geojson() {
            let geojson = JSON.parse(JSON.stringify(this.mapScene2d.flood_geojson))
            if (geojson) {
                geojson.features = geojson.features.filter((f) => {
                    return (
                        f.properties.source === 'regular' &&
                        f.properties.type.toLowerCase() === 'low'
                    )
                })
            }
            return geojson
        },
        flood_regular_medium_geojson() {
            let geojson = JSON.parse(JSON.stringify(this.mapScene2d.flood_geojson))
            if (geojson) {
                geojson.features = geojson.features.filter((f) => {
                    return (
                        f.properties.source === 'regular' &&
                        f.properties.type.toLowerCase() === 'medium'
                    )
                })
            }
            return geojson
        },
        flood_regular_high_geojson() {
            let geojson = JSON.parse(JSON.stringify(this.mapScene2d.flood_geojson))
            if (geojson) {
                geojson.features = geojson.features.filter((f) => {
                    return (
                        f.properties.source === 'regular' &&
                        f.properties.type.toLowerCase() === 'high'
                    )
                })
            }
            return geojson
        },
        flood_insurance_exclusion_geojson() {
            let geojson = JSON.parse(JSON.stringify(this.mapScene2d.flood_geojson))
            if (geojson) {
                geojson.features = geojson.features.filter((f) => {
                    return f.properties.source === 'insurance_exclusion'
                })
            }
            return geojson
        },
        validGeoJsons() {
            // Treat an empty FeatureCollection as invalid in this context
            const validGeoJson = (geojson) =>
                !!(geojson && geojson.features && geojson.features.length)
            return {
                flood_regular_low_geojson: validGeoJson(this.flood_regular_low_geojson),
                flood_regular_medium_geojson: validGeoJson(
                    this.flood_regular_medium_geojson
                ),
                flood_regular_high_geojson: validGeoJson(this.flood_regular_high_geojson),
                flood_insurance_exclusion_geojson: validGeoJson(
                    this.flood_insurance_exclusion_geojson
                ),
                building_geojson: validGeoJson(this.building_geojson),
                parcel_geojson: validGeoJson(this.parcel_geojson),
                address_geojson: validGeoJson(this.address_geojson),
                swimming_pools_geojson: validGeoJson(this.swimming_pools_geojson),
            }
        },
        map_data_ready() {
            return (
                this.mapScene2d.building_geojson !== null &&
                this.mapScene2d.parcel_geojson !== null &&
                this.mapScene2d.address_geojson !== null
            )
        },
        parcel_options() {
            return {
                interactive: this.focusLayer === 'parcels' && !this.readOnly,
                onEachFeature: (feature, layer) => {
                    layer.on({
                        mouseover: (e) => {
                            if (!this.readOnly) {
                                e.target.feature.properties.hover = true
                                this.$refs.parcel_features.mapObject.resetStyle(e.target)
                            }
                        },
                        mouseout: (e) => {
                            e.target.feature.properties.hover = false
                            this.$refs.parcel_features.mapObject.resetStyle(e.target)
                        },
                        click: (e) => {
                            if (!this.readOnly) {
                                this.toggle_parcel(e.target.feature.properties.parcel_id)
                            }
                        },
                    })
                },
                style: (feature) => {
                    var style = {
                        color: '#fff',
                        fillColor: '#fff',
                        weight: 1,
                        opacity: this.focusLayer === 'parcels' ? 1 : 0.4,
                        fillOpacity: this.focusLayer === 'parcels' ? 0.2 : 0,
                    }
                    if (feature.properties.hover === true) {
                        style.fillColor = '#8cf'
                        // style.color = 'var(--color-primary)'
                        style.fillOpacity = 1
                    }
                    if (this.selected_parcel(feature.properties.parcel_id)) {
                        style.weight = 3
                        style.fillOpacity = 0.8
                    }
                    if (this.focusLayer == 'selected') {
                        return {
                            color: 'var(--color-primary)',
                            fillOpacity: 0,
                            opacity: 1,
                            weight: 3,
                        }
                    }
                    return style
                },
            }
        },
        building_options() {
            return {
                interactive: this.focusLayer === 'building' && !this.readOnly,
                onEachFeature: (feature, layer) => {
                    layer.on({
                        mouseover: (e) => {
                            if (!this.readOnly) {
                                e.target.feature.properties.hover = true
                                this.$refs.building_features.mapObject.resetStyle(
                                    e.target
                                )
                                //if (!L.Browser.ie && !L.Browser.opera && !L.Browser.edge) {
                                e.target.bringToFront()
                                //}
                            }
                        },
                        mouseout: (e) => {
                            e.target.feature.properties.hover = false
                            this.$refs.building_features.mapObject.resetStyle(e.target)
                        },
                        click: (e) => {
                            if (this.readOnly) {
                                return
                            } else if (this.focusLayer == 'building') {
                                this.$emit(
                                    'update:buildingId',
                                    e.target.feature.properties.building_id
                                )
                                this.$emit(
                                    'update:address',
                                    e.target.feature.properties.building_id
                                )
                                /*this.$emit(
                                    'update:parcelIds',
                                    e.target.feature.properties.parcel_ids
                                )*/
                            } else if (this.focusLayer == 'secondary-buildings') {
                                /*
                                this.TOGGLE_SECONDARY_BUILDING_ID(
                                    e.target.feature.properties.building_id
                                )*/
                            } else {
                                return
                            }
                        },
                    })
                },
                style: (feature) => {
                    var style = {
                        color: '#ff6f00', // '#357',
                        fillColor: '#ffaf80', //: '#e0e8ff'
                        weight: this.focusLayer === 'building' ? 1 : 0,
                        opacity: 1,
                        fillOpacity: 0.8,
                    }
                    if (feature.properties.hover === true) {
                        style.fillColor = '#fc9'
                        style.color = '#753'
                        style.fillOpacity = 1
                    }
                    if (this.main(feature.properties.building_id)) {
                        style.fillColor = '#ff6f00'
                        if (this.focusLayer === 'building') {
                            style.fillColor = '#ff6f00'
                            style.color = '#fff'
                            if (this.size !== 'small') style.weight = 3
                        } else if (this.focusLayer === 'selected') {
                            style.weight = 0
                            style.fillOpacity = 1
                        } else {
                            style.opacity = 0.6
                            style.weight = 0
                        }
                    } else if (this.secondary(feature.properties.building_id)) {
                        style.fillColor = '#99f'
                        if (this.focusLayer === 'building') {
                            style.fillColor = '#99f'
                            style.color = '#337'
                            style.weight = 3
                        } else {
                            style.opacity = 0.6
                            style.weight = 0
                        }
                    }
                    return style
                },
            }
        },
        address_options() {
            return {
                interactive: false,
                style: {
                    weight: 0,
                    opacity: 0,
                    fillOpacity: 0,
                },
                pointToLayer: function(feature, latlng) {
                    let label = String(feature.properties.streetnumber)
                    return new circleMarker(latlng, {
                        radius: 0,
                        interactive: false,
                    })
                        .bindTooltip(label, {
                            permanent: true,
                            opacity: 1,
                            direction: 'center',
                            className: 'address-labels',
                        })
                        .openTooltip()
                },
            }
        },
        tile_options() {
            return { maxZoom: 20, maxNativeZoom: 18 }
        },
        streetnameTileUrl() {
            let mapping = { 'fr-BE': 'fr', 'nl-BE': 'nl' }
            let normalized_lang = mapping[this.lang]
            normalized_lang = normalized_lang ? normalized_lang : 'fr'
            return `/tiles/streets/streets_${normalized_lang}/{z}/{x}/{y}.png`
        },
        style_swimming_pools() {
            return {
                color: '#ff6f00', // '#357',
                fillColor: '#9cf', //: '#e0e8ff'
                weight: this.focusLayer === 'building' ? 1 : 0,
                opacity: 1,
                fillOpacity: 0.6,
            }
        },

        region() {
            if (this.buildingId === null) {
                return null
            } else if (
                this.buildingId.includes('picc_') ||
                this.buildingId.includes('wal_')
            ) {
                return 'Wallonia'
            } else if (
                this.buildingId.includes('urbis_') ||
                this.buildingId.includes('bru_')
            ) {
                return 'Brussels'
            } else if (
                this.buildingId.includes('grb_') ||
                this.buildingId.includes('vlg_')
            ) {
                return 'Flanders'
            } else {
                return 'France'
            }
        },
    },
    watch: {
        buildingId: {
            immediate: true,
            handler(buildingId, oldBuildingId) {
                if (buildingId !== null) {
                    if (this.map_data_ready) {
                        if (typeof this.$refs.building_features !== 'undefined') {
                            this.$refs.building_features.mapObject.eachLayer((layer) => {
                                this.$refs.building_features.mapObject.resetStyle(layer)
                            })
                        }
                        if (!this.readOnly || this.forceUpdateParcels) {
                            this.auto_select_parcels()
                            this.$nextTick(() => {
                                this.fetch_map_data()
                            })
                        }
                    } else {
                        this.fetch_map_data().then(() => {
                            if (!this.readOnly || this.forceUpdateParcels) {
                                this.auto_select_parcels()
                            }
                        })
                    }
                }
            },
        },
        parcelIds: {
            immediate: true,
            handler(parcelIds, oldParcelIds) {
                let first_parcel_id_changed =
                    parcelIds !== null &&
                    parcelIds.length &&
                    (typeof oldParcelIds === 'undefined' ||
                        oldParcelIds === null ||
                        (oldParcelIds.length && oldParcelIds[0] !== parcelIds[0]))
                if (this.buildingId === null && first_parcel_id_changed) {
                    this.$nextTick(() => {
                        this.fetch_map_data()
                    })
                }
                if (
                    this.map_data_ready &&
                    typeof this.$refs.parcel_features !== 'undefined'
                ) {
                    this.$refs.parcel_features.mapObject.eachLayer((layer) => {
                        this.$refs.parcel_features.mapObject.resetStyle(layer)
                    })
                }
            },
        },
        lang: {
            handler(land, oldLang) {
                this.showStreetNames = false
                this.$nextTick(() => {
                    this.showStreetNames = true
                })
            },
        },
    },
    mounted() {
        if (!this.readOnly) {
            this.auto_select_parcels()
        }
    },
    methods: {
        update_map: debounce(
            function(obj) {
                const bounds = this.$refs.map.mapObject.getBounds()
                const center = this.$refs.map.mapObject.getCenter()
                const zoom = this.$refs.map.mapObject.getZoom()
                this.internalZoom = zoom
                if (zoom >= 18) {
                    const params = {
                        bbox: `${bounds._southWest.lng},${bounds._southWest.lat},${bounds._northEast.lng},${bounds._northEast.lat}`,
                        bbox_crs: 'epsg:4326',
                    }
                    this.fetch_map_data(params)
                } else {
                    this.$emit('update:mapScene2d', {
                        building_geojson: null,
                        parcel_geojson: null,
                        address_geojson: null,
                        swimming_pool_geojson: null,
                        flood_geojson: null,
                    })
                }
            },
            1000,
            { leading: true, trailing: true }
        ),
        main(building_id) {
            return this.buildingId === building_id
        },
        secondary(building_id) {
            return (
                /* TODO: find secondary buildings based on selected parcels
                this.mapScene2d.parcel_geojson.features.find()
                .state.valuationStore.Address.data.features.secondary_building_ids.indexOf(
                    building_id
                ) != -1*/
                false
            )
        },
        selected_parcel(parcel_id) {
            return this.parcelIds.indexOf(parcel_id) != -1
        },
        // new
        fetch_map_data(_params = null) {
            let params = {}
            if (_params != null) {
                params = _params
            } else if (this.buildingId !== null) {
                params.building_id = this.buildingId
            } else if (this.parcelIds) {
                params.parcel_id = this.parcelIds[0]
            } else {
                return
            }
            return axios.get('/map/scene2d', { params }).then((response) => {
                this.$emit('update:mapScene2d', response.data)
            })
        },
        toggle_parcel(parcel_id) {
            let index = this.parcelIds.indexOf(parcel_id)
            let parcel_ids = [...this.parcelIds]
            if (index === -1) {
                parcel_ids.push(parcel_id)
                //state.data.features.parcels.push(parcel)
            } else {
                parcel_ids.splice(index, 1)
                //state.data.features.parcels.splice(index, 1)
            }
            this.$emit('update:parcelIds', parcel_ids)
        },
        auto_select_parcels() {
            let found = false
            if (this.buildingId) {
                for (
                    let i = 0;
                    i < this.mapScene2d.building_geojson.features.length;
                    i++
                ) {
                    if (
                        this.mapScene2d.building_geojson.features[i].properties
                            .building_id == this.buildingId
                    ) {
                        let parcel_ids = this.mapScene2d.building_geojson.features[i]
                            .properties.parcel_ids

                        this.$emit(
                            'update:parcelIds',
                            parcel_ids,
                            this.forceUpdateParcels
                        )

                        found = true
                    }
                }
            }
            return new Promise((res, rej) => {
                res()
            })
        },
        style_flood(feature) {
            let flood_zone_style = this.$config.FLOOD_ZONE_STYLE
            let source_style = flood_zone_style[feature.properties.source]
                ? flood_zone_style[feature.properties.source]
                : flood_zone_style['regular']
            return source_style[feature.properties.type.toLowerCase()]
                ? source_style[feature.properties.type.toLowerCase()]
                : source_style['high']
        },
    },
}
</script>

<style lang="scss">
.b-tooltips {
    .b-tooltip:not(:last-child) {
        margin-right: 0.5em;
    }

    .b-tooltip {
        margin-bottom: 0.5em;
    }
}

.leaflet-tooltip.address-labels {
    background-color: transparent;
    border: transparent;
    box-shadow: none;
}
</style>
